import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useEffect } from "react";
import { setLogin, setLogout } from "../redux/authSlice";
import { useGetUserQuery } from "../slices/usersApiSlice";
import TermsConditions from "./TermsConditions";
import { isTokenValid, isUserRoleAllowed } from "../utils/authHelper";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { userInfo, sessionExpired } = useSelector((state) => state.auth || {});
  const dispatch = useDispatch();
  const location = useLocation();

  const userId = userInfo?.id || userInfo?.user_id;
  const token = userInfo?.token;
  const isAuth = !!(userInfo && token && isTokenValid(token));

  const { data, error, isLoading } = useGetUserQuery(userId || "current", {
    skip: !isAuth || !userId,
  });

  useEffect(() => {
    if (!isAuth) {
      dispatch(setLogout());
    } else if (data && token && (data.id === userId || data.user_id === userId)) {
      dispatch(setLogin({ ...data, token }));
    } else if (error && (error.status === 401 || error.status === 403)) {
      // Only logout on definite unauthorized error
      dispatch(setLogout());
    }
  }, [isAuth, data, error, dispatch, token, userId]);

  if (!isAuth) {
    const sessionExpiredReason =
      (typeof sessionExpired === "object" ? sessionExpired?.message : null) ||
      (sessionExpired ? "Your session timed out due to inactivity. Please log in again." : null);

    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: location,
          sessionExpired: !!sessionExpiredReason,
          reason: sessionExpiredReason,
        }}
      />
    );
  }

  if (isLoading && !userInfo) {
    return <div>Loading...</div>;
  }

  if (!userInfo.termsAccepted && userInfo.termsAccepted !== undefined && userInfo.termsAccepted === false) {
    return <TermsConditions />;
  }

  if (allowedRoles && !isUserRoleAllowed(userInfo, allowedRoles)) {
    return <Navigate to="/unauthorized" replace state={{ from: location }} />;
  }

  return children ? children : <Outlet />;
};

export default ProtectedRoute;
